// <emma-chat-avatar> — the emma logo SVG used in `<emma-chat-turn>` for
// the emma response side. Static rendering only in T08; T11 will add the
// draw-in animation (stroke-dashoffset + fill-opacity per DS chat-bubble.css
// `.chat-avatar.animated`).
//
// SVG path data lifted from design-system/components/chat-bubble.html. The
// gradient ID is randomized at construction time to avoid collisions when
// multiple avatars render in the same DOM (Shadow DOM scopes the SVG, so
// in practice this only matters if a parent renders many turns and they
// all share an outer document).

import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

let avatarGradCounter = 0;

@customElement('emma-chat-avatar')
export class EmmaChatAvatar extends LitElement {
  /** When true, runs the draw-in animation (T11). Currently unused. */
  @property({ type: Boolean })
  animated = false;

  private gradId = `emma-grad-${++avatarGradCounter}`;

  static override styles = css`
    /* :host sizing is driven by CSS vars so an outer class
       (e.g. .chat-turn-collapsed-icon = 20×20 + margin-top 5px)
       can override across the Shadow DOM boundary. CSS custom
       properties inherit through shadow roots. Defaults match
       DS .chat-avatar (24×24, margin-top 2px). */
    :host {
      display: block;
      flex-shrink: 0;
      width: var(--avatar-size, 24px);
      height: var(--avatar-size, 24px);
      margin-top: var(--avatar-margin-top, 2px);
    }
    svg {
      width: 100%;
      height: 100%;
      filter: drop-shadow(0 0 0px transparent);
      transition: filter var(--duration-slow, 400ms)
        var(--easing, cubic-bezier(0.4, 0, 0.2, 1));
    }
    @media (hover: hover) {
      :host(:hover) svg {
        filter: drop-shadow(0 0 8px rgba(48, 83, 247, 0.18));
      }
    }
    /* Static state — DS .chat-avatar.static */
    .logo-main {
      fill-opacity: 1;
      stroke-opacity: 0;
    }
    .logo-shadow {
      fill-opacity: 0.53;
    }
    .logo-glare {
      fill-opacity: 0.2;
    }
  `;

  override render() {
    const id = this.gradId;
    return html`
      <svg viewBox="0 0 29 28" fill="none">
        <defs>
          <linearGradient
            id="${id}"
            x1="25.83"
            y1="1.51"
            x2="10.58"
            y2="27.98"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="white" />
            <stop offset="0.25" stop-color="#23B4FD" />
            <stop offset="0.5" stop-color="#3053F7" />
            <stop offset="0.75" stop-color="#8936FA" />
            <stop offset="1" stop-color="#7E20FF" />
          </linearGradient>
        </defs>
        <path
          class="logo-main"
          fill="url(#${id})"
          stroke="url(#${id})"
          d="M14.0386 0C21.7703 0.000263107 28.0386 6.26818 28.0386 14C28.0386 21.7318 21.7703 27.9997 14.0386 28C12.7899 28 11.5793 27.835 10.4272 27.5283C10.6822 25.9961 11.2267 24.7225 12.2153 24.2676C12.747 24.0652 12.984 23.9425 13.397 23.8848C13.7106 23.8319 15.3993 23.9546 16.062 23.9111C18.042 23.7828 18.5102 22.5239 18.5669 22.0967C18.6234 21.6696 18.2954 21.1612 18.2632 20.6973C18.231 20.2333 18.5826 20.0835 18.7573 19.8389C19.0876 19.0553 18.373 18.9045 18.0435 18.8271C18.7049 18.5864 18.9546 17.9837 18.8696 17.6504C18.2737 17.2732 18.0198 16.6472 18.0366 16.2441C18.0536 15.841 18.2564 15.6784 18.811 15.1338C19.3654 14.5893 19.3981 14.0655 18.5854 13.624C17.9931 13.2819 17.3291 12.7523 16.5493 11.6807C15.7947 10.6447 16.1011 9.96971 15.8745 8.84473C14.6926 4.03983 10.9603 2.01341 6.90967 1.94824C8.99765 0.710573 11.4354 0 14.0386 0Z"
        />
        <path
          class="logo-shadow"
          fill="#438DFC"
          d="M6.922 1.94944C11.1412 1.8697 15.1062 3.86487 16.3312 8.84494C16.5577 9.9698 16.2513 10.6449 17.006 11.6809C17.7857 12.7525 18.4498 13.2812 19.0421 13.6233C19.855 14.0648 19.8214 14.5894 19.2667 15.134C18.7124 15.6784 18.5104 15.8405 18.4933 16.2434C18.4763 16.6465 18.73 17.2733 19.3263 17.6506C19.411 17.984 19.1612 18.5857 18.5001 18.8264C18.8297 18.9038 19.5444 19.0554 19.214 19.8391C19.0393 20.0837 18.6866 20.2335 18.7189 20.6975C18.7512 21.1614 19.0801 21.6699 19.0236 22.0969C18.9668 22.5245 18.4982 23.8964 16.5187 24.0246C15.8564 24.0682 14.1682 23.9456 13.8536 23.9983C13.4406 24.056 13.2036 24.0654 12.672 24.2678C11.6719 24.7279 11.1253 26.0254 10.8741 27.5813L10.463 27.3215C10.7318 25.8821 11.272 24.702 12.2159 24.2678C12.7473 24.0655 12.9838 23.9417 13.3966 23.884C13.7101 23.8311 15.4001 23.9549 16.0626 23.9113C18.0422 23.783 18.5108 22.5243 18.5675 22.0969C18.624 21.6699 18.2951 21.1614 18.2628 20.6975C18.2306 20.2336 18.5822 20.0836 18.757 19.8391C19.0873 19.0556 18.3737 18.9038 18.0441 18.8264C18.705 18.5857 18.955 17.9839 18.8702 17.6506C18.274 17.2733 18.0192 16.6465 18.0363 16.2434C18.0534 15.8405 18.2562 15.6785 18.8107 15.134C19.3653 14.5894 19.398 14.0648 18.5851 13.6233C17.9929 13.2812 17.3294 12.7521 16.5499 11.6809C15.7952 10.6449 16.1016 9.96981 15.8751 8.84494C14.6944 4.0449 10.9679 2.0185 6.922 1.94944Z"
        />
        <path
          class="logo-glare"
          fill="white"
          d="M14 0C21.732 0 28 6.26801 28 14C28 17.1985 26.9262 20.1455 25.1211 22.5029C26.5576 20.2334 27.3906 17.5404 27.3906 14.6523C27.3904 6.78761 21.2203 0.370191 13.4746 0.0107422C13.649 0.0043089 13.8241 4.10459e-07 14 0Z"
        />
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-avatar': EmmaChatAvatar;
  }
}
